import { useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

export type Role = 'host' | 'player' | 'overlay';

export interface CommandResult {
  ok: boolean;
  code?: string;
  message?: string;
  data?: unknown;
}

export interface Connection<V> {
  view: V | null;
  status: 'connecting' | 'live' | 'error' | 'closed';
  error: string | null;
  send: (command: unknown) => Promise<CommandResult>;
  emit: (event: string, payload?: unknown) => Promise<CommandResult>;
}

const REASONS: Record<string, string> = {
  UNAUTHORIZED: 'Dieser Link gilt nicht mehr. Bitte neu beitreten oder den Spielleiter fragen.',
  BAD_HANDSHAKE: 'Der Link ist unvollständig.',
  RATE_LIMIT: 'Zu viele Verbindungsversuche. Bitte einen Moment warten.',
};

/** Hält eine Verbindung zum Spielserver und liefert immer die aktuelle Sicht dieser Rolle. */
export function useGameConnection<V>(code: string | null, role: Role, token: string | null): Connection<V> {
  const [view, setView] = useState<V | null>(null);
  const [status, setStatus] = useState<Connection<V>['status']>('connecting');
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!code || !token) return;
    const socket = io({ auth: { code, role, token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => {
      setStatus('live');
      setError(null);
    });
    socket.on('state', (next: V) => setView(next));
    socket.on('lobby:closed', () => {
      setStatus('closed');
      setError(null);
      socket.disconnect();
    });
    socket.on('connect_error', (err: Error) => {
      setStatus('error');
      setError(REASONS[err.message] ?? 'Keine Verbindung zum Server.');
    });
    socket.on('disconnect', () => {
      setStatus((current) => (current === 'closed' ? 'closed' : 'connecting'));
    });
    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [code, role, token]);

  return useMemo(
    () => ({
      view,
      status,
      error,
      send: (command) =>
        new Promise((resolve) => {
          const socket = socketRef.current;
          if (!socket) return resolve({ ok: false, message: 'Keine Verbindung.' });
          socket.emit('command', command, (r: CommandResult) => resolve(r ?? { ok: false }));
        }),
      emit: (event, payload) =>
        new Promise((resolve) => {
          const socket = socketRef.current;
          if (!socket) return resolve({ ok: false, message: 'Keine Verbindung.' });
          socket.emit(event, payload, (r: CommandResult) => resolve(r ?? { ok: false }));
        }),
    }),
    [view, status, error],
  );
}
